import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import React, { useMemo } from "react";
import { Box, Icon, type IconName, Text } from "@salestrak/ui";
import type { ActivityNode, ActivityChange } from "./activity-types";
import { lifecycleVerb } from "./activity-types";
import { formatTime } from "./activity-utils";
import { EntityTag, OnBehalfLabel } from "#app/components/entity";
import { DateText } from "#app/components/date-text";
import { ActionChunk } from "./chunks";
import { at } from "#app/lib/utils/at";

interface BaseItemProps {
  activity: ActivityNode;
  issueId: string;
  last: boolean;
  bottomSpacing?: boolean;
  /** One line: the sentence truncates with an ellipsis instead of wrapping.
   *  A dashboard card's row; the timeline keeps its wrapping prose. */
  truncate?: boolean;
  /** Whether the sentence ends with " · <time>". Off only for a host that
   *  says the time somewhere else. */
  showTime?: boolean;
  /** How the time reads: `clock` ("5:26 PM") under a day-grouped feed
   *  whose separators carry the date; `relative` ("9h ago", "Yesterday")
   *  where rows from many days sit in one list. The tooltip carries the
   *  full timestamp either way. */
  timeReading?: "clock" | "relative";
  /** Words appended to the sentence after the changes and before the time —
   *  a cross-project feed names the project here (" on <chip>"). */
  trailing?: React.ReactNode;
}

// Change types that always show the user avatar (person-centric actions)
const PERSON_CHANGE_TYPES = new Set(["task", "assignees"]);

// Icon for each change type (stageId handled separately via the stage EntityTag)
const CHANGE_ICONS: Record<string, IconName> = {
  values: "box",
  form: "clipboard",
  note: "message",
  noteReply: "reply",
  name: "pen-line",
  age: "clock",
  issueTypeId: PROPERTY_ICON.issueType,
  address: PROPERTY_ICON.address,
  teams: PROPERTY_ICON.team,
  workflowId: PROPERTY_ICON.workflow,
  trashed: "trash",
  archived: "archive",
  restored: "rotate",
  unarchived: "archive-restore",
};

const ICON_LANE_WIDTH = 20;
const GLYPH_BOX_SIZE = 16;
const SM_LINE_HEIGHT = 20; // text-sm line height — icon lane height pegs icon to first line
const CONNECTOR_TOP_OFFSET = SM_LINE_HEIGHT + 4; // icon lane bottom + 4px gap
const CONNECTOR_BOTTOM_OVERFLOW = 14; // reaches into the marginBottom gap to connect rows
const ICON_CENTER_X = 9.5;
// Inter's visible text sits below the center of its 20px line box. Lift the
// text lane so its glyphs align optically with the centered activity glyph.
const TEXT_OPTICAL_OFFSET = -2;

export const BaseItem = React.memo(function BaseItem({
  activity,
  last,
  bottomSpacing = true,
  truncate = false,
  showTime = true,
  timeReading = "clock",
  trailing,
}: BaseItemProps) {
  // A clock the Stage moved or the cycle ran out on is nobody's act. Older
  // rows name the member who moved the Stage as the author, so the cause,
  // not the author column, decides: those read as System too.
  const triggeredBy = activity.changeset?.age?.after?.triggeredBy;
  const systemDriven =
    triggeredBy === "expired" || triggeredBy === "stageChanged";
  const actor = systemDriven ? null : activity.author;

  const actorName = useMemo(() => {
    if (actor?.name) return actor.name;
    if (systemDriven) return "System";
    return "Unknown";
  }, [actor, systemDriven]);

  const actorAvatar = activity.onBehalf ? (
    <EntityTag
      entity={{ kind: "user", id: "system", user: null }}
      display="visual"
      size="sm"
      system
    />
  ) : (
    <EntityTag
      entity={{
        kind: "user",
        id: actor?.userId ?? "unknown",
        user: actor ?? null,
      }}
      display="visual"
      size="sm"
      interactive={false}
    />
  );

  const changes = useMemo(
    // Drop changes whose chunk would render nothing (assignee/field edits that
    // resolved to no entities) so a grouped line never trails a dangling
    // " and " connector or an actor-only row.
    () => processChangeset(activity).filter(changeRenders),
    [activity],
  );

  if (changes.length === 0) return null;

  // A server activity groups several changes (e.g. a stage move + assignee add)
  // in one changeset — render them as ONE row, not one row per change. The left
  // glyph represents the first change; the text joins every change's chunk.
  const primaryType = at(changes, 0, "change").target.type;
  const showAvatar = PERSON_CHANGE_TYPES.has(primaryType);
  const iconName = CHANGE_ICONS[lifecycleVerb(primaryType) ?? primaryType];

  // " · <time>": the full timestamp is the tooltip, the label lights under
  // its own pointer to say so. Inline in the wrapping timeline; beside the
  // truncating sentence as its own flex item, so the ellipsis on a long
  // row eats the sentence's tail and never the time.
  const when = activity.lastUpdateDate ?? activity.creationDate;
  // Inline, the separator carries its own spaces; as a flex item its leading
  // space would collapse at the item's edge, so the lane's `xs` gap is
  // that space instead.
  const time = showTime ? (
    <Text size="sm" variant="muted" truncate={false}>
      {truncate ? "· " : " · "}
      <DateText
        date={when}
        format={timeReading === "relative" ? "compact" : undefined}
        label={timeReading === "clock" ? formatTime(when) : undefined}
        size="sm"
        truncate={false}
        hoverInk
      />
    </Text>
  ) : null;

  return (
    <Box
      marginBottom={bottomSpacing ? "md" : undefined}
      direction="row"
      gap="sm"
      align="start"
      position="relative"
      className="opacity-95"
    >
      {!last && (
        <Box
          background="border" width={1}
          position="absolute"
          top={CONNECTOR_TOP_OFFSET}
          bottom={-CONNECTOR_BOTTOM_OVERFLOW}
          left={ICON_CENTER_X}
        />
      )}
      <Box
        align="center"
        justify="center"
        width={ICON_LANE_WIDTH}
        height={SM_LINE_HEIGHT}
      >
        <Box
          align="center"
          justify="center"
          width={GLYPH_BOX_SIZE}
          height={GLYPH_BOX_SIZE}
        >
          {activity.onBehalf || showAvatar ? (
            // size="sm" matches the stage glyph's footprint (both 20px); no
            // overflow-clip wrapper — that cropped the avatar down to 16px and
            // made it read smaller than the icon/stage beside it.
            actorAvatar
          ) : primaryType === "stageId" ? (
            <EntityTag
              entity={{
                kind: "stage",
                id: activity.newStage?.stageId ?? "unknown",
                stage: activity.newStage ?? {},
                workflowId: activity.newStage?.workflowId ?? undefined,
              }}
              display="visual"
              size="sm"
              interactive={false}
            />
          ) : iconName ? (
            <Icon
              name={iconName}
              size="md"
              color={primaryType === "workflowId" ? "success" : "muted"}
            />
          ) : (
            actorAvatar
          )}
        </Box>
      </Box>

      {/* Truncating needs the sentence to be a BLOCK: an inline span never
          clips. As a flex item of this box it is one, and `min-w-0` lets it
          shrink; the wrapping timeline keeps the plain block flow. */}
      <Box
        flex={1}
        minWidth={0}
        position="relative"
        top={TEXT_OPTICAL_OFFSET}
        display={truncate ? "flex" : undefined}
        direction={truncate ? "row" : undefined}
        align={truncate ? "baseline" : undefined}
        gap={truncate ? "xs" : undefined}
      >
        <Text size="sm" variant="muted" truncate={truncate}>
          {/* The actor inherits the sentence's quiet ink at rest and lights
              with a pressable `group="row"` host (a dashboard feed row); the
              wrapping timeline has no such group, so nothing changes there. */}
          <Text size="sm" hoverInk="row">
            {activity.onBehalf && actor ? (
              <OnBehalfLabel user={actor} />
            ) : actor?.userId ? (
              <EntityTag
                entity={{ kind: "user", id: actor.userId, user: actor }}
                display="label"
                weight="semibold"
                as="text"
              />
            ) : (
              <Text weight="semibold" size="sm">{actorName}</Text>
            )}
          </Text>
          <Text size="sm"> </Text>
          {changes.map((change, index) => {
            // First chunk has no connector; the last is joined with " and ",
            // any middle ones with ", " ("moved … , edited … and added …").
            const connector =
              index === 0
                ? null
                : index === changes.length - 1
                  ? " and "
                  : ", ";
            return (
              <Text size="sm" key={`${change.target.type}-${index}`}>
                {connector ? <Text size="sm">{connector}</Text> : null}
                <ActionChunk change={change} />
              </Text>
            );
          })}
          {trailing}
          {truncate ? null : time}
        </Text>
        {truncate ? <Box shrink={false}>{time}</Box> : null}
      </Box>
    </Box>
  );
});

/**
 * Whether a change resolves to visible text. The assignee and custom-field
 * chunks render null when their entity/field lists come back empty (e.g. the
 * backend omitted `fields`), so those changes must be dropped before joining —
 * every other change type always renders.
 */
function changeRenders(change: ActivityChange): boolean {
  if (change.target.type === "assignees" || change.target.type === "teams") {
    return (change.users?.length ?? 0) + (change.teams?.length ?? 0) > 0;
  }
  if (change.target.type === "values") {
    return (change.fields?.length ?? 0) > 0;
  }
  return true;
}

/**
 * Process activity changeset into an array of ActivityChange objects.
 */
function processChangeset(activity: ActivityNode): ActivityChange[] {
  // A grouped row opens with its creation: the stored changeset is JSONB,
  // whose keys come back sorted by length, not in the order they happened,
  // so "renamed …, created this project" would otherwise read backwards.
  return Object.keys(activity.changeset ?? {})
    .sort((a, b) => Number(b === "task") - Number(a === "task"))
    .reduce<ActivityChange[]>(
    (changes, changeSubject) => {
      const verb = lifecycleVerb(changeSubject);
      if (changeSubject === "values") {
        const addedFields: string[] = [];
        const removedFields: string[] = [];
        const editedFields: string[] = [];

        Object.keys(activity.changeset[changeSubject]).forEach((fieldId) => {
          const field = activity.changeset[changeSubject][fieldId];
          const afterField = field?.after;
          const beforeField = field?.before;
          if (
            afterField !== undefined &&
            (beforeField === undefined || beforeField === "")
          )
            addedFields.push(fieldId);
          if (afterField === undefined && beforeField !== undefined)
            removedFields.push(fieldId);
          if (afterField !== undefined && beforeField !== undefined) {
            if (!removedFields.includes(fieldId)) editedFields.push(fieldId);
          }
        });

        const fields = activity.fields ?? [];

        if (addedFields.length > 0) {
          changes.push({
            action: "added",
            target: { type: "values", label: "values" },
            fields: fields.filter((f) =>
              addedFields.includes(f.customFieldId),
            ),
          });
        }
        if (removedFields.length > 0) {
          if (!Object.keys(activity.changeset).includes("task")) {
            changes.push({
              action: "removed",
              target: { type: "values", label: "values" },
              fields: fields.filter((f) =>
                removedFields.includes(f.customFieldId),
              ),
            });
          }
        }
        if (editedFields.length > 0) {
          changes.push({
            action: "edited",
            target: { type: "values", label: "values" },
            fields: fields.filter((f) =>
              editedFields.includes(f.customFieldId),
            ),
          });
        }
        return [...changes];
      } else if (changeSubject === "note") {
        if (
          activity.changeset[changeSubject]?.before &&
          !activity.changeset[changeSubject]?.after
        ) {
          changes.push({
            action: "deleted",
            subject: "note",
            target: { type: "note", label: "Note" },
            note: activity.changeset[changeSubject],
          });
          return [...changes];
        }
      } else if (changeSubject === "noteReply") {
        let activityAction = "replied to";
        let activitySubject = "comment";

        Object.keys(activity.changeset[changeSubject]).forEach((noteId) => {
          const note = activity.changeset[changeSubject][noteId];
          if (note.after && note.before) {
            activityAction = "edited";
            activitySubject = "reply";
          }
        });

        changes.push({
          action: activityAction,
          subject: activitySubject,
          target: { type: "noteReply", label: "Note Reply" },
          noteReply: activity.changeset[changeSubject],
        });
        return [...changes];
      } else if (changeSubject === "assignees") {
        const before = activity.changeset[changeSubject].before ?? [];
        const after = activity.changeset[changeSubject].after ?? [];

        const beforeMap = new Map(
          before.map((ref: any) => [
            `${ref.entityType}:${String(ref.entityId)}`,
            ref,
          ]),
        );
        const afterMap = new Map(
          after.map((ref: any) => [
            `${ref.entityType}:${String(ref.entityId)}`,
            ref,
          ]),
        );

        const addedRefs = after.filter(
          (ref: any) =>
            !beforeMap.has(`${ref.entityType}:${String(ref.entityId)}`),
        );
        const removedRefs = before.filter(
          (ref: any) =>
            !afterMap.has(`${ref.entityType}:${String(ref.entityId)}`),
        );

        const users = activity.users ?? [];
        const teams = activity.teams ?? [];

        if (addedRefs.length > 0) {
          const addedUserIds = addedRefs
            .filter((ref: any) => ref.entityType === "user")
            .map((ref: any) => String(ref.entityId));
          const addedTeamIds = addedRefs
            .filter((ref: any) => ref.entityType === "team")
            .map((ref: any) => String(ref.entityId));

          changes.push({
            action: "added",
            target: { type: "assignees", label: "assignee" },
            users: users.filter((u) =>
              addedUserIds.includes(String(u.userId)),
            ),
            teams: teams.filter((t) =>
              addedTeamIds.includes(String(t.teamId)),
            ),
          });
        }

        if (removedRefs.length > 0) {
          const removedUserIds = removedRefs
            .filter((ref: any) => ref.entityType === "user")
            .map((ref: any) => String(ref.entityId));
          const removedTeamIds = removedRefs
            .filter((ref: any) => ref.entityType === "team")
            .map((ref: any) => String(ref.entityId));

          changes.push({
            action: "removed",
            target: { type: "assignees", label: "assignee" },
            users: users.filter((u) =>
              removedUserIds.includes(String(u.userId)),
            ),
            teams: teams.filter((t) =>
              removedTeamIds.includes(String(t.teamId)),
            ),
          });
        }
        return [...changes];
      } else if (changeSubject === "teams") {
        // The project's Teams: bare team ids, shown by name.
        const before: string[] = activity.changeset[changeSubject].before ?? [];
        const after: string[] = activity.changeset[changeSubject].after ?? [];
        const teams = activity.teams ?? [];
        const pick = (ids: string[]) =>
          teams.filter((t) => ids.includes(String(t.teamId)));
        const added = pick(after.filter((id) => !before.includes(id)));
        const removed = pick(before.filter((id) => !after.includes(id)));
        if (added.length > 0) {
          changes.push({ action: "added", target: { type: "teams", label: "team" }, teams: added });
        }
        if (removed.length > 0) {
          changes.push({ action: "removed", target: { type: "teams", label: "team" }, teams: removed });
        }
        return [...changes];
      } else if (changeSubject === "stageId") {
        return [
          ...changes,
          {
            action: "updated",
            target: { type: changeSubject, label: changeSubject },
            oldValue: {
              stage: activity.oldStage,
              workflow: activity.oldWorkflow,
            },
            newValue: {
              stage: activity.newStage,
              workflow: activity.newWorkflow,
            },
          },
        ];
      } else if (changeSubject === "age") {
        // The chunk words the change by its cause (`triggeredBy`): a cycle
        // edit carries no status at all, so nothing here defaults one.
        const { before, after } = activity.changeset[changeSubject];
        return [
          ...changes,
          {
            action: "updated",
            subject: after?.triggeredBy ?? undefined,
            target: { type: changeSubject, label: changeSubject },
            oldValue: { status: before?.status ?? null, cycle: before?.cycle ?? null },
            newValue: { status: after?.status ?? null, cycle: after?.cycle ?? null },
          },
        ];
      } else if (verb) {
        // The changeset carries the issue id as `after`; the sentence needs
        // only the verb.
        changes.push({
          action: verb,
          target: { type: verb, label: verb },
          oldValue: { value: undefined },
          newValue: { value: undefined },
        });
        return [...changes];
      }

      // Default handling
      const { before, after } = activity.changeset[changeSubject] ?? {};
      let action = "";
      if (before === undefined && after !== undefined) action = "created";
      else if (before !== undefined && after === undefined) action = "deleted";
      else if (before !== undefined && after !== undefined) action = "updated";

      const target: ActivityChange["target"] = {
        type: changeSubject,
        label: changeSubject,
      };

      if (changeSubject === "task") {
        target.referenceId = activity.issueId;
      }

      const extraData: Partial<ActivityChange> = {};
      if (changeSubject === "stageId") {
        extraData.oldStage = activity.oldStage;
        extraData.newStage = activity.newStage;
        extraData.oldWorkflow = activity.oldWorkflow;
        extraData.newWorkflow = activity.newWorkflow;
      } else if (changeSubject === "issueTypeId") {
        extraData.oldType = activity.oldType;
        extraData.newType = activity.newType;
      } else if (changeSubject === "address") {
        extraData.oldAddress = activity.oldAddress;
        extraData.newAddress = activity.newAddress;
      }

      return [
        ...changes,
        {
          action,
          target,
          oldValue: { value: before },
          newValue: { value: after },
          ...extraData,
        },
      ];
    },
    [],
  );
}
